import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Check, ExternalLink, Link2, Link2Off, Pencil, Plus, RefreshCw, Send, ShieldCheck, Sparkles, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { CreateLedgerAccountDialog, isSpendAccount } from "@/components/CreateLedgerAccountDialog";
import { ExpenseLinesEditor } from "@/components/ExpenseLinesEditor";
import { cardLabel } from "@/components/ExpensesTable";
import { OrgMemberSelect, memberLabel } from "@/components/OrgMemberSelect";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCurrency, formatDate, formatDateTime, formatTime } from "@/lib/format";
import { InfoHint } from "@/components/InfoHint";
import { PageContainer, PageHeader, BackLink } from "@/components/PagePrimitives";
import {
  HeaderRow,
  ScoreMeter,
  TH,
  prettyCategory,
  prettyMerchant,
} from "@/components/ExpenseUi";
import { cn } from "@/lib/utils";
import { ExpenseStateBadge, TransactionStatusBadge, VatReclaimBadge, flagLabel } from "@/components/ExpenseStatus";
import { versionLabel } from "@/lib/expense-approval-policy-utils";
import { usePermissions } from "@/lib/permissions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CopyButton } from "@/components/ui/copy-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { components } from "@dolfin/api-admin";

type MerchantDetailResponse = components["schemas"]["MerchantDetailResponse"];
type ExpenseResponse = components["schemas"]["ExpenseResponse"];
type ReceiptResponse = components["schemas"]["ReceiptResponse"];
type MatchCandidateResponse = components["schemas"]["MatchCandidateResponse"];
type ExpenseAuditLogResponse = components["schemas"]["ExpenseAuditLogResponse"];
type LedgerAccountResponse = components["schemas"]["LedgerAccountResponse"];
type AccountingTaxRateResponse = components["schemas"]["AccountingTaxRateResponse"];
type ExpenseCodingSource = components["schemas"]["ExpenseCodingSource"];
type OrganisationUserResponse = components["schemas"]["OrganisationUserResponse"];

// What matters: the person's own decision, something learned from their decisions, or a default nobody chose.
function codingSourceText(source: ExpenseCodingSource | undefined, category: string | null): string | null {
  switch (source) {
    case "Human":
      return "You chose this account.";
    case "OrgMapping":
      return `Your rule for ${category ?? "this category"}.`;
    case "History":
      return "Learned from similar expenses you've coded.";
    case "ClientMapping":
      return `The default account for ${category ?? "this category"}.`;
    case "CardDefault":
      return "This card's default account.";
    case "Personal":
      return "Personal spend, so it goes to your director's loan account.";
    default:
      return null;
  }
}

// The audit trail uses machine-readable event names. Mapping them here keeps internal vocabulary out
// of a screen a client's own customers may see.
const AUDIT_EVENT_LABELS: Record<string, string> = {
  "expense.captured": "Captured from the card network",
  "expense.receipt_attached": "Receipt attached",
  "expense.learned_coding": "Coded from your history",
  "expense.flagged": "Flagged for review",
  "expense.pending_review": "Waiting for review",
  "expense.posted": "Posted to accounting",
  "expense.post_failed": "Posting failed",
};

// Where a line's coding came from, in words. The enum names (MccDefault, ReceiptLine) are the API's
// vocabulary, not a reviewer's.
const LINE_SOURCE_LABELS: Record<string, string> = {
  ReceiptLine: "From receipt",
  MccDefault: "Category default",
  Manual: "Edited by hand",
  Balancing: "Rounding balance",
};

// A single non-receipt line carries the expense's account, so its source is HOW that account was chosen.
const CODED_LINE_SOURCE_LABELS: Partial<Record<ExpenseCodingSource, string>> = {
  History: "Learned",
  OrgMapping: "Your rule",
  ClientMapping: "Category default",
  CardDefault: "Card default",
  Personal: "Personal",
};

const lineSourceLabel = (source?: string | null, codingSource?: ExpenseCodingSource) =>
  (source === "MccDefault" && codingSource ? CODED_LINE_SOURCE_LABELS[codingSource] : undefined) ??
  (source ? LINE_SOURCE_LABELS[source] : undefined) ??
  source ??
  "-";

// Older flag entries are prefixed "MissingAccountingConfiguration: …"; the heading already says it was flagged.
const auditDetail = (entry: { event?: string | null; detail?: string | null }) =>
  entry.event === "expense.flagged"
    ? (entry.detail ?? "").replace(/^[A-Z][A-Za-z]+: /, "")
    : entry.detail;

const auditEventLabel = (event?: string | null) =>
  (event ? AUDIT_EVENT_LABELS[event] : undefined) ?? event ?? "Updated";

// Laid out by the decision: a flag first, with what to do; coding next, as it is what posts; the raw transaction last.
export function ExpenseDetailPage({
  expenseId,
  onBack,
  onOpenCards,
  onOpenCategoryMapping,
  onOpenReceipt,
  onOpenExpense,
  onOpenMerchant,
  onOpenApprovalPolicies,
  booksCard,
}: {
  expenseId: string;
  /** The host's Books card (S6), when the client holds `books`. */
  booksCard?: ReactNode;
  onBack?: () => void;
  /** Where to fix a card's accounts — offered when the flag is a missing clearing account. */
  onOpenCards?: () => void;
  /** Where to map a merchant category — offered when the flag is an unmapped category. */
  onOpenCategoryMapping?: () => void;
  /** Opens the attached receipt — the reverse of the receipt page's "Open the expense". */
  onOpenReceipt?: (receiptId: string) => void;
  /** Opens another expense — the similar expenses a learned account was based on. */
  onOpenExpense?: (expenseId: string) => void;
  onOpenMerchant?: (merchantId: string) => void;
  /** Opens the expense approval policies - offered beside the policy that decided this expense. */
  onOpenApprovalPolicies?: () => void;
}) {
  const api = useDolfinClient();
  const { can } = usePermissions();
  const canResolvePolicyViolations = can("resolvePolicyViolations");
  const canAssignSpender = can("assignSpenders");
  // A demo organisation reviews expenses but changes none (D7); the server refuses these writes too.
  const [expense, setExpense] = useState<ExpenseResponse | null>(null);
  const [receipt, setReceipt] = useState<ReceiptResponse | null>(null);
  const [candidates, setCandidates] = useState<MatchCandidateResponse[]>([]);
  const [history, setHistory] = useState<ExpenseAuditLogResponse[]>([]);
  // The line carries only the account id; a reviewer needs to see where it posts.
  const [accountNames, setAccountNames] = useState<Map<string, string>>(new Map());
  // Expense accounts only: what a person can choose to code spend to.
  const [expenseAccounts, setExpenseAccounts] = useState<LedgerAccountResponse[]>([]);
  const [taxRates, setTaxRates] = useState<AccountingTaxRateResponse[]>([]);
  const [editingLines, setEditingLines] = useState(false);
  // The connected platform's name, for "Create an account": the person should know where it will live.
  const [platformName, setPlatformName] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  // The merchant with every spelling of it, for "Also seen as".
  const [merchant, setMerchant] = useState<MerchantDetailResponse | null>(null);
  // Who the applied policy is waiting on, by name rather than id.
  const [pendingApprovers, setPendingApprovers] = useState<OrganisationUserResponse[]>([]);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [memo, setMemo] = useState("");

  // Every await below can finish after the user has moved on to another expense. Each result is checked
  // against the expense it was started for, so a slow response for A never lands on B.
  const currentExpenseId = useRef(expenseId);
  currentExpenseId.current = expenseId;

  const load = useCallback(async () => {
    const stale = () => currentExpenseId.current !== expenseId;
    setLoading(true);
    const { data, error } = await api.GET("/v1/expenses/{id}", {
      params: { path: { id: expenseId } },
    });
    if (stale()) return;

    if (error || !data) {
      toast.error(apiErrorMessage(error, "Couldn't load the expense."));
      setLoading(false);
      return;
    }

    setExpense(data);
    setMemo(data.memo ?? "");
    if (data.merchant?.id) {
      api
        .GET("/v1/merchants/{id}", { params: { path: { id: data.merchant.id } } })
        .then(({ data: m }) => {
          if (!stale()) setMerchant(m ?? null);
        });
    } else {
      setMerchant(null);
    }

    const approverIds = data.pendingApproverUserIds ?? [];
    if (approverIds.length > 0) {
      // By id: at most five approvers, and a page of members would miss anyone past it.
      Promise.all(
        approverIds.map((id) =>
          api.GET("/v1/users/{id}", { params: { path: { id } } }).then(
            ({ data: u }) =>
              (u
                ? { id, email: u.email, name: u.name }
                : { id, email: "Member not found" }) as OrganisationUserResponse,
          ),
        ),
      ).then((users) => {
        if (!stale()) setPendingApprovers(users);
      });
    } else {
      setPendingApprovers([]);
    }

    if (data.primaryReceiptId) {
      const { data: r } = await api.GET("/v1/receipts/{id}", {
        params: { path: { id: data.primaryReceiptId } },
      });
      if (stale()) return;
      setReceipt(r ?? null);
      setCandidates([]);
    } else {
      setReceipt(null);
      // Only fetch candidates when there is no receipt - they are the answer to "which receipt is
      // this", and that question is closed once one is attached.
      const { data: c } = await api.GET("/v1/expenses/{id}/match-candidates", {
        params: { path: { id: expenseId } },
      });
      if (stale()) return;
      setCandidates(c ?? []);
    }

    const [{ data: logs }, { data: accounts }, { data: connection }, { data: rates }] = await Promise.all([
      api.GET("/v1/expenses/{id}/audit-logs", { params: { path: { id: expenseId } } }),
      api.GET("/v1/accounting/ledger-accounts", { params: { query: {} } }),
      api.GET("/v1/accounting/connection", {}),
      api.GET("/v1/accounting/tax-rates", {}),
    ]);
    if (stale()) return;
    setHistory(logs ?? []);
    setTaxRates(rates ?? []);
    setExpenseAccounts((accounts ?? []).filter(isSpendAccount));
    setPlatformName(connection?.serviceDisplayName ?? null);
    setCompanyName(connection?.companyName ?? null);
    setAccountNames(
      new Map(
        (accounts ?? []).map((a) => [a.id!, a.displayId ? `${a.displayId} · ${a.name}` : (a.name ?? "")]),
      ),
    );

    setLoading(false);
  }, [api, expenseId]);

  useEffect(() => {
    load();
  }, [load]);

  async function act(
    label: string,
    run: () => Promise<{ error?: unknown }>,
    successMessage: string,
  ) {
    setActing(true);
    const { error } = await run();
    if (error) {
      toast.error(apiErrorMessage(error, `Couldn't ${label}.`));
    } else {
      toast.success(successMessage);
      await load();
    }
    setActing(false);
  }

  // An admin's correction of who spent it: a shared card, or a card that changed hands. Re-runs approval
  // routing on spend not yet posted, since a policy may be scoped to the person.
  function changeSpender(userId: string | null) {
    if (!userId || userId === expense?.spender?.userId) return;
    act(
      "change who spent it",
      () => api.PUT("/v1/expenses/{id}/spender", { params: { path: { id: expenseId } }, body: { userId } }),
      "Spender updated.",
    );
  }

  // Coding this expense also teaches learned coding, unless a category rule covers it (the rule always wins),
  // so the message says which. The pickers' last option opens "Create an account".
  const CREATE_ACCOUNT = "__create_account__";
  function onPickAccount(value: string) {
    if (value === CREATE_ACCOUNT) setCreatingAccount(true);
    else chooseAccount(value);
  }

  async function chooseAccount(ledgerAccountId: string, confirmOnly = false, knownName?: string) {
    if (!expense) return;
    // Every line moves to the chosen account, keeping its own amount, description and VAT, so a receipt's split survives.
    const current = expense.lines ?? [];
    const lines =
      current.length > 0
        ? current.map((line) => ({
            amount: Number(line.amount ?? 0),
            ledgerAccountId,
            description: line.description ?? null,
            taxCode: line.taxCode ?? null,
            taxRate: line.taxRate ?? null,
            taxAmount: line.taxAmount ?? null,
          }))
        : [{ amount: Number(expense.amount ?? 0), ledgerAccountId }];
    const accountName = knownName ?? accountNames.get(ledgerAccountId) ?? "that account";
    const category = expense.spendCategoryLabel ? prettyCategory(expense.spendCategoryLabel) : "this category";
    const ruleWins = expense.codingSource === "OrgMapping";
    await act(
      "change the account",
      () =>
        api.PUT("/v1/expenses/{id}/lines", {
          params: { path: { id: expenseId } },
          body: { lines },
        }),
      confirmOnly
        ? `Confirmed. ${accountName} will be used more confidently for similar spend.`
        : ruleWins
          ? `Changed for this expense only. Your rule still codes other ${category} spend to its mapped account.`
          : `Coded to ${accountName}. Similar spend will follow this choice from now on.`,
    );
  }

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center gap-2 p-10 text-sm text-muted-foreground">
          <Spinner className="size-4" /> Loading expense...
        </div>
      </PageContainer>
    );
  }

  if (!expense) {
    return (
      <PageContainer>
        <p className="text-destructive text-sm">That expense could not be found.</p>
      </PageContainer>
    );
  }

  const transaction = expense.cardTransaction;
  const showPolicyReasoning = !!expense.approvalReasoning && expense.flagReason !== "PolicyViolation";
  const showPendingApprovers = expense.state === "PendingReview" && pendingApprovers.length > 0;
  const linesTotal = (expense.lines ?? []).reduce((sum, l) => sum + Number(l.amount ?? 0), 0);

  return (
    <PageContainer>
      {onBack ? <BackLink onClick={onBack}>Back to expenses</BackLink> : null}

      <PageHeader
        eyebrow="Expenses"
        title={expense.merchant?.displayName ?? (expense.merchantName ? prettyMerchant(expense.merchantName) : "Card expense")}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-base font-semibold text-foreground tabular-nums">
              {formatCurrency(expense.amount, expense.currency)}
            </span>
            <span>{formatDate(expense.transactionDate ?? expense.createdAt)}</span>
            <span aria-hidden>·</span>
            <span>
              {expense.cardDisplayName ? `${expense.cardDisplayName}, ` : ""}
              {cardLabel(expense)}
            </span>
            {expense.spender ? (
              <>
                <span aria-hidden>·</span>
                <span>{memberLabel(expense.spender)}</span>
              </>
            ) : null}
            {expense.spendCategoryLabel ? (
              <>
                <span aria-hidden>·</span>
                <span>{prettyCategory(expense.spendCategoryLabel)}</span>
              </>
            ) : null}
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <ExpenseStateBadge state={expense.state} />
            {expense.state === "PendingReview" ? (
              <Button
                size="sm"
                disabled={acting}
                onClick={() =>
                  act(
                    "approve the expense",
                    () => api.POST("/v1/expenses/{id}/approve", { params: { path: { id: expenseId } } }),
                    "Approved. Sending to your accounting platform.",
                  )
                }
              >
                <Check className="size-4" /> Approve
              </Button>
            ) : null}
            {/* Retry only. An expense in Coded is waiting for its card transaction to settle and posts
                itself once the final amount arrives - there is nothing useful to press. */}
            {expense.state === "PostFailed" ? (
              <Button
                size="sm"
                disabled={acting}
                onClick={() =>
                  act(
                    "retry the post",
                    () =>
                      api.POST("/v1/expenses/{id}/retry-post", {
                        params: { path: { id: expenseId } },
                      }),
                    "Sending to your accounting platform again.",
                  )
                }
              >
                <Send className="size-4" /> Retry post
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              disabled={acting}
              onClick={() =>
                act(
                  "re-code the expense",
                  () => api.POST("/v1/expenses/{id}/recode", { params: { path: { id: expenseId } } }),
                  "Re-coded against your current mappings.",
                )
              }
            >
              <RefreshCw className="size-4" /> Re-code
            </Button>
          </div>
        }
      />

      <div className="flex flex-col gap-4">
      {expense.flagReason ? (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <TriangleAlert className="size-4 text-amber-600" />
              {flagLabel(expense.flagReason)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">{expense.flagDetail}</p>
            {expense.flagReason === "TaxRateUnresolved" ? (
              <p className="text-sm text-muted-foreground">
                Each line posts at one of your accounting platform&apos;s tax rates, so the VAT on it can be reclaimed.
                The lines marked below could not be matched to one: hover the mark to see why. Check the VAT read
                from the receipt against the receipt itself. The expense will not post until every line has a rate.
              </p>
            ) : null}
            {expense.flagReason === "PolicyViolation" ? (
              <p className="text-sm text-muted-foreground">
                {canResolvePolicyViolations
                  ? "One of your expense approval policies flags spend like this. Check it against your policy, then accept it to let it post."
                  : "One of your expense approval policies flags spend like this. An organisation admin needs to accept it before it can post."}
              </p>
            ) : null}
            {expense.flagReason === "UnmappedMcc" ? (
              <p className="text-sm text-muted-foreground">
                Choose an account for this expense and similar spend will follow your choice. To code every{" "}
                {expense.spendCategoryLabel ? prettyCategory(expense.spendCategoryLabel) : "such"} purchase the
                same way, map the whole category instead.
              </p>
            ) : null}
            {/* Fixing the setup there re-codes this expense on its own; accepting can't help, as there is still nowhere to post. */}
            <div className="flex flex-wrap gap-2">
              {expense.flagReason === "MissingAccountingConfiguration" && onOpenCards ? (
                <Button size="sm" onClick={onOpenCards}>
                  Set up {cardLabel(expense)}
                </Button>
              ) : null}
              {expense.flagReason === "UnmappedMcc" ? (
                <Select disabled={acting} value="" onValueChange={onPickAccount}>
                  <SelectTrigger size="sm" className="h-8 w-64 border-amber-500/50 bg-background">
                    <SelectValue placeholder="Choose an account" />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseAccounts.map((account) => (
                      <SelectItem key={account.id} value={account.id!}>
                        {account.displayId ? `${account.displayId} · ` : ""}
                        {account.name}
                      </SelectItem>
                    ))}
                    <SelectItem value={CREATE_ACCOUNT} className="text-primary">
                      <Plus className="size-3.5" /> Create an account…
                    </SelectItem>
                  </SelectContent>
                </Select>
              ) : null}
              {expense.flagReason === "UnmappedMcc" && onOpenCategoryMapping ? (
                <Button size="sm" variant="outline" onClick={onOpenCategoryMapping}>
                  Map the whole category
                </Button>
              ) : null}
              {expense.flagReason !== "MissingAccountingConfiguration" &&
              (expense.flagReason !== "PolicyViolation" || canResolvePolicyViolations) ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={acting}
                  onClick={() =>
                    act(
                      "resolve the flag",
                      () =>
                        api.POST("/v1/expenses/{id}/resolve-flag", {
                          params: { path: { id: expenseId } },
                        }),
                      "Resolved.",
                    )
                  }
                >
                  Accept and continue
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Which approval policy decided this expense, and why - the answer to "why is this waiting on me". */}
      {expense.appliedApprovalPolicy || expense.approvalReasoning || pendingApprovers.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2 text-base">
              <ShieldCheck className="size-4 text-muted-foreground" />
              {expense.appliedApprovalPolicy ? (
                <>
                  <span>Policy: {expense.appliedApprovalPolicy.name}</span>
                  <Badge variant="secondary" className="px-1.5 font-mono text-[10px] font-normal">
                    {versionLabel(expense.appliedApprovalPolicy.version)}
                  </Badge>
                </>
              ) : (
                <span>No approval policy matched</span>
              )}
              {onOpenApprovalPolicies ? (
                <Button variant="link" size="sm" className="ml-auto h-auto p-0 text-xs" onClick={onOpenApprovalPolicies}>
                  View policies
                </Button>
              ) : null}
            </CardTitle>
          </CardHeader>
          {/* A policy violation already shows this reasoning in the flag card above; once is enough. With
              nothing left to say, the card is just its title row rather than an empty box. */}
          {showPolicyReasoning || showPendingApprovers ? (
            <CardContent className="space-y-2 text-sm">
              {showPolicyReasoning ? <p className="text-muted-foreground">{expense.approvalReasoning}</p> : null}
              {showPendingApprovers ? (
                <p>
                  <span className="text-muted-foreground">Waiting for </span>
                  {pendingApprovers.map((u) => u.name?.trim() || u.email).join(", ")}
                  <span className="text-muted-foreground"> or an admin to approve.</span>
                </p>
              ) : null}
            </CardContent>
          ) : null}
        </Card>
      ) : null}

      {expense.postedAt ? <PostedEntryCard expense={expense} /> : null}

      {booksCard}

      {expense.postFailureReason ? (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardHeader>
            <CardTitle className="text-base">Your accounting platform rejected this</CardTitle>
          </CardHeader>
          <CardContent>
            {/* The platform's own words, verbatim - "account is archived" is actionable in a way that
                "post failed" is not. */}
            <p className="text-sm text-muted-foreground">{expense.postFailureReason}</p>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Receipt</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {receipt ? (
              <>
                {/* How the pairing happened is the first question when it looks wrong. */}
                <p className="text-xs text-muted-foreground">
                  {expense.matchSource === "Manual"
                    ? "Attached by hand."
                    : expense.matchSource === "Auto"
                      ? `Matched automatically${
                          expense.matchScore != null
                            ? ` with ${Math.round(Number(expense.matchScore) * 100)}% confidence`
                            : ""
                        }.`
                      : null}
                </p>
                <dl className="grid grid-cols-2 gap-2 text-sm">
                  <dt className="text-muted-foreground">Merchant</dt>
                  <dd>{receipt.merchantName || "-"}</dd>
                  <dt className="text-muted-foreground">Date</dt>
                  <dd>{formatDate(receipt.receiptDate)}</dd>
                  <dt className="text-muted-foreground">Total</dt>
                  <dd className="tabular-nums">
                    {formatCurrency(receipt.total ?? undefined, receipt.currency)}
                  </dd>
                  <dt className="text-muted-foreground">VAT</dt>
                  <dd className="tabular-nums">
                    {formatCurrency(receipt.taxTotal ?? undefined, receipt.currency)}
                  </dd>
                  <dt className="text-muted-foreground">Supplier VAT number</dt>
                  <dd>
                    {receipt.merchantVatNumber ? (
                      receipt.merchantVatNumber
                    ) : (
                      // Called out rather than left blank: without it the VAT on this spend cannot be
                      // reclaimed, and it is the one gap a person can actually close.
                      <span className="text-amber-600">
                        Not on the receipt - ask the merchant for a VAT invoice
                      </span>
                    )}
                  </dd>
                </dl>
                <div className="flex flex-wrap gap-2">
                  {onOpenReceipt && receipt.id ? (
                    <Button variant="outline" size="sm" onClick={() => onOpenReceipt(receipt.id!)}>
                      <Link2 className="size-4" /> Open the receipt
                    </Button>
                  ) : null}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={acting}
                    onClick={() =>
                      act(
                        "detach the receipt",
                        () =>
                          api.DELETE("/v1/expenses/{id}/receipt", {
                            params: { path: { id: expenseId } },
                          }),
                        "Receipt detached. The expense falls back to category coding.",
                      )
                    }
                  >
                    <Link2Off className="size-4" /> Detach receipt
                  </Button>
                </div>
              </>
            ) : candidates.length > 0 ? (
              <>
                <p className="text-sm text-muted-foreground">
                  These uploaded receipts might belong to this expense.
                </p>
                <div className="space-y-2">
                  {candidates.map((candidate) => (
                    <div
                      key={candidate.receiptId}
                      className="flex items-center justify-between rounded-md border p-3"
                    >
                      <div className="flex min-w-0 items-center gap-3 text-sm">
                        <div className="min-w-0">
                          <div className="truncate font-medium">{prettyMerchant(candidate.merchantName)}</div>
                          <div className="text-xs text-muted-foreground">
                            <span className="tabular-nums">
                              {formatCurrency(candidate.amount ?? undefined, expense.currency)}
                            </span>{" "}
                            · {formatDate(candidate.date)}
                            {!candidate.withinAmountTolerance ? (
                              <span className="text-amber-600"> · amount differs</span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <ScoreMeter score={Number(candidate.score ?? 0)} />
                        <Button
                          size="sm"
                          disabled={acting}
                          onClick={() =>
                            act(
                              "attach the receipt",
                              () =>
                                api.POST("/v1/expenses/{id}/attach-receipt", {
                                  params: { path: { id: expenseId } },
                                  body: { receiptId: candidate.receiptId! },
                                }),
                              "Receipt attached and the expense re-coded.",
                            )
                          }
                        >
                          <Link2 className="size-4" /> Attach
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                No receipt attached, and nothing uploaded that looks like a match. This expense still
                posts to your accounts - a receipt only sharpens the VAT position.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {expense.merchant ? (
              <MerchantSummary
                merchant={merchant}
                fallbackName={expense.merchant.displayName}
                cardSpelling={expense.merchantName}
                onOpen={onOpenMerchant ? () => onOpenMerchant(expense.merchant!.id!) : undefined}
              />
            ) : null}

            <div className="space-y-2">
              <Label htmlFor="memo">Memo</Label>
              <div className="flex gap-2">
                <Input
                  id="memo"
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  placeholder="Note for your accountant"
                />
                <Button
                  variant="outline"
                  disabled={acting || memo === (expense.memo ?? "")}
                  onClick={() =>
                    act(
                      "save the memo",
                      () =>
                        api.PATCH("/v1/expenses/{id}", {
                          params: { path: { id: expenseId } },
                          body: { memo },
                        }),
                      "Memo saved.",
                    )
                  }
                >
                  Save
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <div className="text-sm font-medium">Personal spend</div>
                <p className="text-xs text-muted-foreground">
                  Posts to your director's loan account instead of an expense account, and blocks VAT
                  reclaim.
                </p>
              </div>
              <Switch
                checked={expense.isPersonal ?? false}
                disabled={acting}
                onCheckedChange={(checked) =>
                  act(
                    "update the expense",
                    () =>
                      api.PATCH("/v1/expenses/{id}", {
                        params: { path: { id: expenseId } },
                        body: { isPersonal: checked },
                      }),
                    checked ? "Marked as personal spend." : "Marked as business spend.",
                  )
                }
              />
            </div>

          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-base">Coding</CardTitle>
            {codingSourceText(expense.codingSource, expense.spendCategoryLabel ? prettyCategory(expense.spendCategoryLabel) : null) ? (
              <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                {expense.codingSource === "History" ? <Sparkles className="size-3.5 text-primary" /> : null}
                {codingSourceText(expense.codingSource, expense.spendCategoryLabel ? prettyCategory(expense.spendCategoryLabel) : null)}
                {expense.codingConfirmed && expense.codingSource !== "Human" ? (
                  <Badge variant="secondary" className="ml-1">Confirmed</Badge>
                ) : null}
              </p>
            ) : null}
          </div>
          {!editingLines && expense.state !== "Posting" && expense.state !== "Posted" ? (
          <div className="flex flex-wrap items-center gap-2">
          {/* One account for the whole expense. Shown while every line shares an account, including an
              itemised receipt's lines; a split across several accounts is the lines editor's job. */}
          {new Set((expense.lines ?? []).map((l) => l.ledgerAccountId ?? "")).size <= 1 &&
          expense.flagReason !== "UnmappedMcc" ? (
            <Select
              disabled={acting}
              value={expense.lines?.[0]?.ledgerAccountId ?? undefined}
              onValueChange={onPickAccount}
            >
              <SelectTrigger size="sm" className="h-8 w-64">
                <SelectValue placeholder="Choose an account" />
              </SelectTrigger>
              <SelectContent>
                {expenseAccounts.map((account) => (
                  <SelectItem key={account.id} value={account.id!}>
                    {account.displayId ? `${account.displayId} · ` : ""}
                    {account.name}
                  </SelectItem>
                ))}
                <SelectItem value={CREATE_ACCOUNT} className="text-primary">
                  <Plus className="size-3.5" /> Create an account…
                </SelectItem>
              </SelectContent>
            </Select>
          ) : null}
            <Button size="sm" variant="outline" className="h-8" disabled={acting} onClick={() => setEditingLines(true)}>
              <Pencil className="size-3.5" /> Edit lines
            </Button>
          </div>
          ) : null}
        </CardHeader>
        <CardContent>
          {/* The evidence behind a learned account, so a reviewer can judge it rather than trust it. */}
          {expense.codingSource === "History" && expense.learnedCoding ? (
            <div className="mb-4 rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
              <p className="font-medium">
                {expense.learnedCoding.similarExpenses?.length === 1
                  ? `You coded a similar expense to ${expense.learnedCoding.ledgerAccountName}.`
                  : `You coded ${expense.learnedCoding.similarExpenses?.length ?? 0} similar expenses, ${Math.round(Number(expense.learnedCoding.voteShare ?? 0) * 100)}% of them to ${expense.learnedCoding.ledgerAccountName}.`}
              </p>
              {/* Agreeing with a learned account is a decision learning needs, without having to Approve (which also posts). */}
              {expense.state !== "Posting" && expense.state !== "Posted" && expense.learnedCoding.ledgerAccountId ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={acting}
                  onClick={() => chooseAccount(expense.learnedCoding!.ledgerAccountId!, true)}
                >
                  <Check className="size-4" /> Looks right
                </Button>
              ) : null}
              </div>
              <ul className="mt-2 space-y-1 text-muted-foreground">
                {(expense.learnedCoding.similarExpenses ?? []).map((n) => (
                  <li key={n.expenseId} className="flex flex-wrap gap-x-2">
                    {onOpenExpense ? (
                      <button
                        type="button"
                        className="text-foreground underline-offset-2 hover:underline"
                        onClick={() => onOpenExpense(n.expenseId!)}
                      >
                        {prettyMerchant(n.merchantName)}
                      </button>
                    ) : (
                      <span className="text-foreground">{prettyMerchant(n.merchantName)}</span>
                    )}
                    {/* Why one decision was enough: it was the same merchant, however the card spelled it. */}
                    {n.sameMerchant ? (
                      <Badge variant="outline" className="border-primary/30 px-1.5 py-0 text-[10px] text-primary">
                        Same merchant
                      </Badge>
                    ) : null}
                    <span className="tabular-nums">{formatCurrency(n.amount, n.currency)}</span>
                    <span>{formatDate(n.transactionDate)}</span>
                    <span aria-hidden>·</span>
                    <span>{n.ledgerAccountName ?? "No account"}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {editingLines ? (
            <ExpenseLinesEditor
              expense={expense}
              accounts={expenseAccounts}
              taxRates={taxRates}
              onCancel={() => setEditingLines(false)}
              onSaved={async () => {
                setEditingLines(false);
                await load();
              }}
            />
          ) : (
          <>
          <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <HeaderRow>
                <TableHead className={TH}>Description</TableHead>
                <TableHead className={TH}>Account</TableHead>
                <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
                <TableHead className={cn(TH, "text-right")}>VAT</TableHead>
                <TableHead className={TH}>Tax rate</TableHead>
                <TableHead className={TH}>Reclaim</TableHead>
                <TableHead className={TH}>Source</TableHead>
              </HeaderRow>
            </TableHeader>
            <TableBody>
              {(expense.lines ?? []).map((line) => (
                <TableRow key={line.id} className={cn(line.taxRateIssue && "bg-amber-500/5")}>
                  <TableCell>{line.description || "-"}</TableCell>
                  <TableCell>
                    {line.ledgerAccountId ? (
                      (accountNames.get(line.ledgerAccountId) ?? "Unknown account")
                    ) : (
                      <span className="text-amber-600">Not set</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(line.amount, expense.currency)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {line.taxAmount != null
                      ? formatCurrency(line.taxAmount, expense.currency)
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {line.taxRateIssue ? (
                      <span className="inline-flex items-center gap-1 text-amber-600">
                        No rate
                        <InfoHint label="Why this line has no tax rate">{line.taxRateIssue}</InfoHint>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">{line.taxRateName || line.taxCode || "-"}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <VatReclaimBadge
                      reclaimable={line.isVatReclaimable}
                      blockedReason={line.vatReclaimBlockedReason}
                    />
                  </TableCell>
                  <TableCell className="text-muted-foreground">{lineSourceLabel(line.source, expense.codingSource)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>

          {/* The invariant made visible. If these ever disagree the expense will not post, so showing
              it here beats discovering it in a rejection. */}
          <div className="flex justify-end pt-3 text-sm">
            <span
              className={cn(
                "text-muted-foreground",
                Math.abs(linesTotal - Number(expense.amount ?? 0)) > 0.005 && "text-destructive",
              )}
            >
              Lines total{" "}
              <span className="tabular-nums text-foreground">
                {formatCurrency(linesTotal, expense.currency)}
              </span>{" "}
              of {formatCurrency(expense.amount, expense.currency)}
            </span>
          </div>
          </>
          )}
        </CardContent>
      </Card>

      {/* Answers "how did this receipt end up on this transaction" and "why did it post when it did". */}
      {history.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">History</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="relative">
              {history.map((entry, i) => (
                <li key={entry.id} className="relative flex gap-4 pb-5 text-sm last:pb-0">
                  {/* Newest first; the newest event's dot is filled. */}
                  {i < history.length - 1 ? (
                    <span aria-hidden className="absolute top-3 left-[5px] h-full w-px bg-border" />
                  ) : null}
                  <span
                    aria-hidden
                    className={cn(
                      "relative mt-1.5 size-[11px] shrink-0 rounded-full border-2",
                      i === 0 ? "border-accent-blue bg-accent-blue" : "border-border bg-background",
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                      <span className="font-medium">{auditEventLabel(entry.event)}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {formatDate(entry.createdAt)} · {formatTime(entry.createdAt)}
                      </span>
                    </div>
                    {entry.detail ? (
                      <p className="mt-0.5 text-muted-foreground">{auditDetail(entry)}</p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      ) : null}

      {transaction ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Card transaction</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-sm @3xl:grid-cols-[8rem_minmax(0,1fr)_8rem_minmax(0,1fr)]">
              <dt className="text-muted-foreground">Card</dt>
              <dd>
                {expense.cardDisplayName ? `${expense.cardDisplayName} · ` : ""}
                {cardLabel(expense)}
              </dd>
              <dt className="flex items-center gap-1.5 text-muted-foreground">
                Spent by
                <InfoHint label="How the spender is set">
                  The card's holder when the spend came in. An organisation admin can change it, for a shared
                  card or one that changed hands.
                </InfoHint>
              </dt>
              <dd>
                {canAssignSpender ? (
                  <OrgMemberSelect
                    value={expense.spender?.userId ?? null}
                    placeholder="Unassigned"
                    disabled={acting}
                    aria-label="Spent by"
                    className="h-8 max-w-56"
                    onChange={changeSpender}
                  />
                ) : expense.spender ? (
                  memberLabel(expense.spender)
                ) : (
                  <span className="text-muted-foreground">Unassigned</span>
                )}
              </dd>
              <dt className="text-muted-foreground">Descriptor</dt>
              <dd className="font-mono text-xs">{transaction.descriptor || "-"}</dd>
              <dt className="text-muted-foreground">Status</dt>
              <dd>
                <TransactionStatusBadge status={transaction.status} />
              </dd>
              <dt className="text-muted-foreground">Category code</dt>
              <dd>
                {transaction.mcc ? `${transaction.mcc} · ${prettyCategory(transaction.mccLabel) ?? ""}` : "-"}
              </dd>
              <dt className="text-muted-foreground">Occurred</dt>
              <dd>{formatDateTime(transaction.transactionOccurrenceDate)}</dd>
              {transaction.acceptorCity || transaction.acceptorCountry ? (
                <>
                  <dt className="text-muted-foreground">Where</dt>
                  <dd className="capitalize">
                    {[transaction.acceptorCity?.toLowerCase(), transaction.acceptorCountry]
                      .filter(Boolean)
                      .join(", ")}
                  </dd>
                </>
              ) : null}
              <dt className="text-muted-foreground">Authorised</dt>
              <dd className="tabular-nums">
                {transaction.transactionHoldAmount != null
                  ? formatCurrency(
                      transaction.transactionHoldAmount,
                      transaction.transactionCurrency,
                    )
                  : "-"}
              </dd>
              <dt className="text-muted-foreground">Settled</dt>
              <dd className="tabular-nums">
                {/* Keyed on status: the settled amount is always a number and reads 0.00 until the
                    network settles, which looked like a zero-value charge. */}
                {transaction.status === "Pending"
                  ? "Not yet settled"
                  : formatCurrency(transaction.transactionSettledAmount, transaction.transactionCurrency)}
              </dd>
              {transaction.billingCurrency &&
              transaction.billingCurrency !== transaction.transactionCurrency &&
              transaction.status === "Settled" ? (
                <>
                  <dt className="text-muted-foreground">Billed to card</dt>
                  <dd className="tabular-nums">
                    {formatCurrency(transaction.billingSettledAmount, transaction.billingCurrency)}
                  </dd>
                </>
              ) : null}
            </dl>
          </CardContent>
        </Card>
      ) : null}
      </div>
      <CreateLedgerAccountDialog
        open={creatingAccount}
        onOpenChange={setCreatingAccount}
        platformName={platformName}
        companyName={companyName}
        ruleCoversCategory={expense.codingSource === "OrgMapping"}
        onCreated={(account) => {
          // Straight into the pickers and onto this expense: the whole point was not to leave the page.
          setExpenseAccounts((current) => [...current, account]);
          setAccountNames((current) => new Map(current).set(account.id!, account.displayId ? `${account.displayId} · ${account.name}` : account.name));
          chooseAccount(account.id!, false, account.name);
        }}
      />
    </PageContainer>
  );
}

// Other spellings, so "ZETTLE_*BLUEANC" visibly counts as the Blue Anchor (and a wrong match can be spotted).
function MerchantSummary({
  merchant,
  fallbackName,
  cardSpelling,
  onOpen,
}: {
  merchant: MerchantDetailResponse | null;
  fallbackName?: string | null;
  cardSpelling?: string | null;
  onOpen?: () => void;
}) {
  const others = (merchant?.aliases ?? [])
    .flatMap((a) => (a.seenAs?.length ? a.seenAs : [a.descriptor]).map((text) => ({ text, method: a.method })))
    .filter((s) => s.text !== cardSpelling);

  return (
    <div className="rounded-md border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium">{merchant?.displayName ?? fallbackName ?? "Merchant"}</div>
          <p className="text-xs text-muted-foreground">
            {merchant ? `${merchant.expenseCount} expense${merchant.expenseCount === 1 ? "" : "s"} here` : "Merchant"}
            {cardSpelling ? (
              <>
                {" · the card shows "}
                <span className="font-mono">{cardSpelling}</span>
              </>
            ) : null}
          </p>
        </div>
        {onOpen ? (
          <Button size="sm" variant="outline" onClick={onOpen}>
            View merchant
          </Button>
        ) : null}
      </div>
      {others.length > 0 ? (
        <div className="mt-2 text-xs text-muted-foreground">
          Also seen as{" "}
          {others.map((s, i) => (
            <span key={s.text}>
              {i > 0 ? ", " : ""}
              <span className="font-mono text-foreground">{s.text}</span>
              {s.method === "Similar" ? " (recognised automatically)" : ""}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// Apideck's service id, as the platform calls itself.
function accountingPlatformName(platform: string | null | undefined): string {
  switch (platform) {
    case "quickbooks":
      return "QuickBooks";
    case "xero":
      return "Xero";
    case null:
    case undefined:
    case "":
      return "your accounting platform";
    default:
      return platform.charAt(0).toUpperCase() + platform.slice(1);
  }
}

// Each platform shows the reference in a different field, so the hint names it.
function PostedEntryCard({ expense }: { expense: ExpenseResponse }) {
  const platform = accountingPlatformName(expense.accountingPlatform);
  const searchField =
    expense.accountingPlatform === "quickbooks" ? "Ref no." : expense.accountingPlatform === "xero" ? "Reference" : null;

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <CardTitle className="text-base">Posted to {platform}</CardTitle>
          <p className="text-sm text-muted-foreground">{formatDateTime(expense.postedAt!)}</p>
        </div>
        {expense.accountingUrl ? (
          <Button variant="outline" size="sm" asChild>
            <a href={expense.accountingUrl} target="_blank" rel="noreferrer">
              <ExternalLink className="size-4" /> Open in {platform}
            </a>
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          {expense.accountingReference ? (
            <div className="space-y-1">
              <dt className="text-muted-foreground">Reference</dt>
              <dd className="flex items-center gap-2">
                <span className="font-mono">{expense.accountingReference}</span>
                <CopyButton value={expense.accountingReference} />
              </dd>
              <dd className="text-xs text-muted-foreground">
                {searchField
                  ? `Shown as the ${searchField} in ${platform} - search for it there.`
                  : `Search ${platform} for it to find the entry.`}
              </dd>
            </div>
          ) : expense.apideckExpenseId ? (
            // Posted before references were recorded: the platform's own id is all there is.
            <div className="space-y-1">
              <dt className="text-muted-foreground">{platform} id</dt>
              <dd className="flex items-center gap-2">
                <span className="font-mono">{expense.apideckExpenseId}</span>
                <CopyButton value={expense.apideckExpenseId} />
              </dd>
            </div>
          ) : null}
          {expense.paidFromAccountName ? (
            <div className="space-y-1">
              <dt className="text-muted-foreground">Paid from</dt>
              <dd>{expense.paidFromAccountName}</dd>
            </div>
          ) : null}
        </dl>
      </CardContent>
    </Card>
  );
}
